import { useRef } from 'react';
import { cn } from '@/lib/utils/cn';
import IconCheck from '@/assets/icons/ic-check.svg';

interface UsernameInputProps {
    id: string;
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    hintText?: string;
    isChecking?: boolean;
    isAvailable?: boolean | null;
}

export const UsernameInput = ({
    id,
    name,
    value,
    onChange,
    onBlur,
    placeholder = 'username',
    disabled = false,
    error = false,
    hintText,
    isChecking = false,
    isAvailable = null,
}: UsernameInputProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleWrapperClick = (e: React.MouseEvent) => {
        if (!disabled && inputRef.current && e.target !== inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Show status indicator on the right side
    const showStatusIndicator = value.length >= 5 && !error;

    return (
        <div>
            <div
                className={cn(
                    // Base styles matching StanInput wrapper
                    'border-disabled relative my-1 flex h-12 w-full items-center rounded-md border px-4 py-3 transition duration-150 ease-in-out',
                    // Hover state
                    !disabled && 'hover:border-brand-main',
                    // Focus state
                    'focus-within:border-brand-main focus-within:shadow-[var(--shadow-material-focus-glow-default)]',
                    // Error state
                    error && 'border-negative-main bg-negative-dimmed',
                    error && !disabled && 'hover:border-negative',
                    error &&
                        'focus-within:border-negative-intense focus-within:shadow-[var(--shadow-material-focus-glow-error)]',
                    // Disabled state
                    disabled && 'bg-neutral-disabled pointer-events-none cursor-not-allowed border-transparent'
                )}
                onClick={handleWrapperClick}
            >
                <span
                    className={cn(
                        'text-disabled mr-3 text-lg font-normal',
                        error && 'text-negative-fg-negative-bold',
                        disabled && 'opacity-50'
                    )}
                >
                    @
                </span>
                <span
                    className={cn(
                        'text-neutral-black text-base leading-[1.375] font-medium whitespace-nowrap',
                        disabled && 'text-disabled'
                    )}
                >
                    stan.store/
                </span>
                <input
                    ref={inputRef}
                    id={id}
                    name={name}
                    type='text'
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    disabled={disabled}
                    placeholder={placeholder}
                    className={cn(
                        'placeholder:text-disabled flex-1 border-none bg-transparent outline-none',
                        'text-dark text-base leading-[1.375]',
                        disabled && 'text-disabled cursor-not-allowed'
                    )}
                />
                {showStatusIndicator && (
                    <div className='pointer-events-none inset-y-auto flex items-center justify-center max-md:absolute max-md:right-2 md:ml-5'>
                        {isChecking && (
                            <div className='border-neutral-secondary size-4 animate-spin rounded-full border border-t-transparent' />
                        )}
                        {!isChecking && isAvailable === true && <IconCheck className='size-5' />}
                    </div>
                )}
            </div>
            {hintText && (
                <span
                    className={cn(
                        'text-disabled text-caption-2-regular! mt-2.5 block',
                        error && 'text-negative-fg-negative-moderate'
                    )}
                >
                    {hintText}
                </span>
            )}
        </div>
    );
};
